import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PencilIcon, PlusIcon, TrashIcon } from '@/components/catalog/icons';
import { FormErrorSummary } from '@/components/catalog/FormShell';
import { CatalogErrorBlock, CatalogMasterCard } from '@/components/catalog-admin/CatalogMasterBits';
import { Notice } from '@/components/ui/Notice';
import { ApiError, api } from '@/lib/api';
import {
  BILLING_REGION_NONE,
  TARGET_BILLING_REGIONS,
  useBillingRegionLabels,
  type BillingRegionLabeller,
} from '@/lib/billing-region';
import { pluralise } from '@/lib/format';
import { denialReason, type Ability } from '@/lib/permissions';
import {
  TARGET_ALREADY_EXISTS,
  TARGET_METRICS,
  baseCurrencyFor,
  billingRegionsFor,
  describeTargetPeriod,
  formatTarget,
  isTargetAmount,
  metricIsMoney,
  targetMetricLabel,
} from '@/lib/targets';
import type {
  Target,
  TargetAttribution,
  TargetBillingRegion,
  TargetCreateInput,
  TargetMetric,
  TargetPeriodRecord,
  TargetSubjectGroup,
  TargetUpdateInput,
  UserGroup,
} from '@/lib/types';
import {
  Badge,
  Button,
  Combobox,
  type ComboboxOption,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Modal,
  MultiCombobox,
  type MultiComboboxOption,
  Select,
  Skeleton,
  ValuePills,
  namePills,
} from '@/components/ui';

/* -------------------------------------------------------------------------
 * Revenue targets set against a USER GROUP, managed from /catalog.
 *
 * Migration 124, owner's decision 2026-08-31. `f_revenue_targets` gained a
 * `subject_group_id` beside its `subject_id`, and `TARGET_SUBJECT_TYPES` gained
 * a third member.
 *
 * ## Why here, and why a card of its own
 *
 * A group target belongs to nobody in particular, so there is no person's row to
 * hang it off — `UserTargetsModal` is per person and reachable only from
 * `/users`. It sits beside Target periods for the same reason that card does: a
 * group and a window are both business-wide vocabulary that everybody's figures
 * read through, and both are configured rather than owned.
 *
 * It is deliberately NOT a `MASTERS` or `PERIODS` registry entry on
 * `CatalogPage`. Its row is neither a `ReferenceRecord` (no `sortOrder`, no
 * `name`, no `isActive` — it is a target) nor a `PeriodRow`, and every registry
 * entry is handed a New button and a delete by construction. Folding it in would
 * auto-grant verbs from the shape of the registry rather than from what the
 * endpoint actually offers, which is the same argument the billing-region card
 * makes from the other direction.
 *
 * ## Gating: administrator, and the 403 is still handled
 *
 * ⚠️ **`/targets` carries NO `requireRole`** — `targets.routes.ts` says so at
 * length, and it is the third module in this API (after `/tasks` and `/notes`)
 * gated on permission sets with a scope instead. READ is held by every set that
 * can sign in; WRITE is the `administrator` grant alone.
 *
 * 🔴 **THE PROXY IS GONE — corrected 2026-09-04.** *SUPERSEDED: "So
 * `adminWriteDenial(ability)` here is a PROXY for a grant, not a reading of the
 * route's guard, and it is the right proxy for exactly one reason: the
 * administrator grant is what the admin role is provisioned with."* It was
 * `ability.isAdmin`, and `adminWriteDenial` asks about **`reference-data`** — a
 * module these endpoints are not in and never were.
 *
 * `targets` is in `OPEN_WRITE_MODULES`, so `canCreate` / `canUpdate` /
 * `canDelete('targets')` resolve against the SET and are the real answer. The
 * proxy's own escape clause is what condemns it: the grant "can be taken off an
 * administrator's set with one UPDATE and no deploy", and on that day the role
 * check offers three controls the server refuses. It also refused a non-admin
 * who genuinely HOLDS the grant — the silent direction, with no error to
 * explain the missing button.
 *
 * ⚠️ **Three grants, asked separately.** A set can grant `targets.update` and
 * refuse `targets.delete`; one flag cannot say so.
 *
 * ⚠️ **Every write below STILL handles a 403** — a grant can be withdrawn under
 * a live access token. The gate is the optimisation; the refusal is the
 * guarantee.
 *
 * Controls are gated; VALUES are not. The card, the list and the figures render
 * for every reader, because a group target is the number a team works to and a
 * member who cannot see it cannot tell whether their own share is large or
 * small. That is the owner's ruling of 2026-08-31, and it is why
 * `visibleToRow` in `targets.service.ts` answers `true` for `user_group` the
 * same way it does for `organisation`.
 *
 * ## 🔴 EVERY TEAM TARGET NAMES ONE OR MORE BILLING BANDS — a personal one names none
 *
 * Owner's decision. A team commits to a figure **per region**, not overall, so at
 * least one band is REQUIRED on this form, and `TargetBillingRegion` has **five**
 * members: the four bands plus `'none'`, which is the unbanded companies — the
 * ones with no country recorded. ⚠️ **`'none'` is a real choice, not the absence
 * of one**, so it is an ordinary row in the picker and never the empty option.
 *
 * 🔴 **SEVERAL BANDS AT ONCE SINCE 2026-09-15 — owner's decision.** *SUPERSEDED:
 * "`billingRegion` is a REQUIRED field on this form", a single-select.* "UK + EU"
 * is `united_kingdom` AND `europe`, and until that day it could not be stored at
 * all. The control is a `MultiCombobox` and the authority on a read is
 * {@link Target.billingRegions}.
 *
 * ⛔ **STOP READING `billingRegion` FOR DISPLAY — it is a PROXY FIELD now.** It
 * is the stored column verbatim: a single slug on every target that existed
 * before that day, and a **comma-joined string** (`'europe,united_kingdom'`) on a
 * multi-band one. Rendering it through a label lookup prints that literal string
 * as an unrecognised slug. Every render site below reads `billingRegions`.
 *
 * ⚠️ **THREE STATES, AND TWO OF THEM ARE ONE KEYSTROKE APART.** `null` is *not
 * region scoped* — a `user` or `organisation` target, which this form never
 * makes; `['europe']` is one band; `['europe','united_kingdom']` is a set.
 * **`billingRegions` is NEVER `[]` on the wire**, so a reader testing
 * `.length === 0` would conflate *no bands* with *not scoped*. In this FORM `[]`
 * is a third thing again — *nobody has ticked anything yet* — which `validate()`
 * refuses and `billingRegionsFor` turns into the `null` the server refuses.
 *
 * ⚠️ **`UserTargetsModal` is the mirror image and must stay so.** A personal
 * target is OVERALL and carries no band at all — that form cannot express one,
 * structurally, and its comment says why.
 *
 * ⚠️ **THE BAND SET IS PART OF THE UNIQUE KEY, so two targets for one group,
 * metric and period are ORDINARY when their band sets differ** — exactly as they
 * already were when only their currencies differed. That is why the 409 copy
 * below names the bands, and why every row on the list prints its own: two rows
 * that differ only by band would otherwise render identically, and a reader would
 * take one for a duplicate and remove it.
 *
 * 🔴 **AND TWO BAND SETS MAY OVERLAP — that is ALLOWED, not a defect.** A target
 * on `['united_kingdom','europe']` and one on `['europe']` are different rows
 * under that key, and **their actuals double-count the same invoices**. Nothing
 * resolves two targets; they are read by name, which is the same ruling this
 * codebase took on overlapping target periods. ⛔ **There is deliberately no
 * client-side overlap check** — a frontend convention presented as a rule is what
 * this repo refuses everywhere. What the list does instead is STATE the overlap
 * where it exists; see `overlappingBandNote`.
 *
 * ## The one thing that must not be got wrong
 *
 * ⚠️ **A group that cannot be measured must never render as 0%, as an empty bar
 * or as a dash that reads like a figure.** `delivery_lead` and `approvers` name
 * no relationship any revenue document records, so the server returns before any
 * metric query runs and answers `actual: null` with
 * `attribution.kind === 'unattributable'`. The sentence saying why is composed
 * SERVER-SIDE and is rendered here VERBATIM — this file must never compose its
 * own, because doing so would mean holding a copy of which slugs attribute, and
 * that map lives in `target-attribution.ts` where the schema can be seen.
 * --------------------------------------------------------------------- */

/* --- the write grants ---------------------------------------------------- */

/**
 * Whether this account may write a group target AT ALL — the Manage/View label
 * on the card, and nothing finer.
 *
 * The three verbs are asked separately everywhere they decide a control; this
 * one exists because the card's label is a single word and the honest answer to
 * "is there anything in here for you to change" is the union of them.
 */
function canWriteAnyGroupTarget(ability: Ability): boolean {
  return (
    ability.canCreate('targets') || ability.canUpdate('targets') || ability.canDelete('targets')
  );
}

/**
 * Why a group-target write is refused, or `null` when it is not.
 *
 * ⚠️ **`denialReason` is asked about `update` in every case, deliberately.** Its
 * `delete` branch answers *"Deleting is restricted to administrators"*, which is
 * true of the other thirteen modules and **false of this one** — a target's
 * delete is decided by the grant, not by the tier. Asking about `update` reaches
 * only sentences that are true here, and keeps the wording in its one home
 * rather than growing a second copy in this file.
 *
 * ⚠️ It returns `null` for an ADMIN even when the server refuses, because
 * `can()` short-circuits the admin tier. That is why every caller pairs it with
 * a fallback naming what happened rather than why.
 */
function targetsWriteDenial(ability: Ability): string | null {
  return denialReason(ability, 'update', 'targets');
}

/** `/targets` caps `perPage` at 200 (default 50). Not the entities' 100. */
const TARGETS_QUERY = {
  page: 1,
  perPage: 200,
  subjectType: 'user_group',
  sort: 'periodStart',
  order: 'asc',
} as const;

/** `/target-periods` has its own ceiling again: 1..200, default 50. */
const PERIODS_PAGE_SIZE = 200;

/** The whole currency master in one request — its ceiling is 250, not 200. */
const CURRENCY_PAGE_SIZE = 250;

const SKELETON_ROWS = 3;

/**
 * The empty row list, as ONE value.
 *
 * A fresh `[]` per render is a new identity every time, which makes every
 * `useMemo` and `useEffect` downstream of it fire on every render — the grouping
 * below and the attribution probe both hang off this.
 */
const NO_TARGETS: readonly Target[] = [];

/** Uppercased before the test, so a typed "gbp" is accepted rather than scolded. */
const CURRENCY_PATTERN = /^[A-Z]{3}$/;

/*
 * 🔴 **THE FIVE BANDS MOVED TO `@/lib/billing-region` — 2026-09-14.**
 * *SUPERSEDED, a local `const TARGET_BILLING_REGIONS = [...BILLING_REGIONS,
 * BILLING_REGION_NONE]` with its own doc.* Same value, one owner:
 * `FinancialAnalyticsPanel` had composed the identical list for its own picker,
 * and two compositions of one vocabulary are two places a sixth band has to
 * arrive — one of which would be missed.
 *
 * The reasoning is unchanged and now lives at the export rather than being
 * restated here: a picker built from `BILLING_REGIONS` alone is silently short
 * by the band easiest to forget and hardest to notice, `'none'` sorts last as
 * the catch-all, and the order is the master's `sortOrder` rather than the SQL
 * ENUM's.
 *
 * ⛔ **AND THERE IS NO `BILLING_REGION_LABELS` TO IMPORT — it does not exist and
 * must not be created.** The four names are admin-editable columns on
 * `f_billing_region_defaults`, so they are FETCHED: `useBillingRegionLabels()`
 * is the resolver, and every label below comes from it. A literal map would be a
 * second copy of wording the server owns — correct on the day it is written and
 * silently wrong after the first rename, with nothing failing.
 */

/**
 * What the unbanded band IS, said on the option itself.
 *
 * ⚠️ **NOT HELPER TEXT, and it is not the exception either — it is out of that
 * rule's scope entirely.** The prohibition forbids prose attached to a CONTROL;
 * an option-object `hint` inside a `Combobox`/`MultiCombobox` list LABELS ONE
 * OPTION, renders inside the popover beside the row it describes, and is part of
 * that row's searchable text. A sweep must not take it.
 *
 * ⚠️ **The wrong conclusion it prevents is the one this whole feature turns on**:
 * four of the five rows are places and the fifth reads as an absence, so without
 * it a reader takes `'none'` for "no band" — the empty selection — and leaves the
 * companies with no country of record committed against nothing while the four
 * real bands quietly fail to add up to the business.
 */
const UNBANDED_BAND_HINT =
  'The companies with no country of record — a population to commit against, not an empty choice.';

/**
 * Whether two band sets hold the same members, ORDER-INDEPENDENTLY.
 *
 * ⚠️ **Required, not tidiness.** The server serves `billingRegions` SORTED —
 * that canonical order is what makes its unique key still mean *the same set* —
 * and a picker holds them in TICK order. A sequence comparison would therefore
 * report an untouched target as edited the moment the two disagreed, enable Save
 * over a form nobody changed, and re-assert a band set on every unrelated edit.
 */
function sameBandSet(
  a: readonly TargetBillingRegion[],
  b: readonly TargetBillingRegion[],
): boolean {
  if (a.length !== b.length) return false;
  const seen = new Set<string>(b);
  return a.every((band) => seen.has(band));
}

/**
 * The `billingRegions` to SEND for a subject type — the plural counterpart of
 * `billingRegionFor` in `@/lib/targets`, and **the DECISION is still that
 * module's**: `subjectTypeCarriesBillingRegion` is what says which subject types
 * carry a band, and this only owns the arity.
 *
 * ⚠️ **Callers must not write `subjectType` to a payload without also writing
 * this.** The server validates the POST-MERGE state and refuses the
 * contradiction rather than auto-clearing, so a lone `subjectType` is a 422
 * about a field nobody touched — the same pair rule `baseCurrencyFor` carries
 * for `{metric, baseCurrency}`.
 *
 * ⛔ **NOTHING IS INVENTED FROM AN EMPTY SET: it answers `null` and lets the
 * server refuse.** Defaulting to `['none']` there would be the most damaging
 * line available in this feature — `'none'` is a REAL population, so an
 * unanswered control would silently commit a number against the companies nobody
 * has banded, save cleanly, and read back as a deliberate choice. **An empty
 * control is not a value.**
 *
 * ✅ **IT LIVES IN `@/lib/targets` NOW, beside `billingRegionFor`** — imported
 * rather than declared here. ⚠️ *SUPERSEDED: "It is copied in `UserTargetsModal`
 * and belongs in `@/lib/targets` … recorded as a follow-up rather than done
 * here."* Both local copies are gone and **the empty-set rule they held went
 * INTO the library rather than being lost on the way** — the
 * `financial-shared.ts` precedent, where retiring a duplicate keeps the stronger
 * of the two behaviours.
 *
 * ⛔ **DO NOT REDECLARE IT HERE.** Two spellings of "what goes in
 * `billingRegions`" would agree the day they were written and drift on the first
 * correction, and what would drift is whether an untouched control silently
 * commits a number against the companies nobody has banded.
 */

/**
 * The bands a row is set over, as one readable phrase — or `null` to print
 * nothing.
 *
 * ⚠️ **It reads {@link Target.billingRegions} and NEVER `billingRegion`**, which
 * is a proxy field: on a multi-band row that column is a comma-joined string, and
 * putting it through `regionLabel` prints `europe,united_kingdom` as an
 * unrecognised slug.
 *
 * ⚠️ **`null` prints NOTHING rather than a band.** A stored row predating the
 * column carries no bands, and naming one — `'none'` most of all, which is a real
 * band — would be this list inventing a commitment. The empty cell is what sends
 * somebody to the edit form, where the field is required. `[]` is not a state the
 * wire can hold and is treated the same way.
 */
function describeBands(
  bands: readonly TargetBillingRegion[] | null,
  regionLabel: BillingRegionLabeller,
): string | null {
  if (bands === null || bands.length === 0) return null;
  return bands.map((band) => regionLabel(band)).join(', ');
}

/**
 * Whether two targets in one group measure the same invoices twice.
 *
 * Same metric, same window, and band sets that INTERSECT — a target on
 * `['united_kingdom','europe']` beside one on `['europe']` counts every European
 * invoice against both. Different metrics measure different things and different
 * windows cover different months, so neither is an overlap.
 *
 * ⛔ **This is a STATEMENT, never a refusal.** Overlapping band sets are
 * different rows under the unique key and the owner decided they are allowed —
 * the same ruling this codebase took on overlapping target periods, where a
 * client-side check would refuse the very rows the feature exists to hold.
 */
function bandsOverlapWithin(targets: readonly Target[]): boolean {
  for (let i = 0; i < targets.length; i += 1) {
    const first = targets[i];
    const firstBands = first?.billingRegions;
    if (first === undefined || firstBands === null || firstBands === undefined) continue;
    const held = new Set<string>(firstBands);
    for (let j = i + 1; j < targets.length; j += 1) {
      const second = targets[j];
      const secondBands = second?.billingRegions;
      if (second === undefined || secondBands === null || secondBands === undefined) continue;
      if (second.metric !== first.metric) continue;
      if (second.period.id !== first.period.id) continue;
      if (secondBands.some((band) => held.has(band))) return true;
    }
  }
  return false;
}

const stroke = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

/**
 * A person beside a bullseye — a target somebody is measured against.
 *
 * Deliberately neither `UserTargetsModal`'s plain bullseye nor
 * `TargetPeriodsCard`'s calendar: three things on this screen are about targets
 * and they answer three different questions, and identical marks are how a
 * reader conflates a window with a commitment.
 */
function GroupTargetIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <circle cx="16" cy="8" r="5" />
      <circle cx="16" cy="8" r="1.5" />
      <circle cx="7.5" cy="9.5" r="2.5" />
      <path d="M3 20v-1.5A3.5 3.5 0 0 1 6.5 15h2A3.5 3.5 0 0 1 12 18.5V20" />
    </svg>
  );
}

/**
 * The target list.
 *
 * `forbidden` is its own member rather than an error string: `targets.read` is a
 * grant, and a refusal to say is a different fact from a read that failed.
 */
type TargetsState =
  | { kind: 'loading' }
  | { kind: 'ready'; targets: Target[]; total: number }
  | { kind: 'forbidden' }
  | { kind: 'error'; message: string };

/**
 * The windows, fetched UNFILTERED and split at render.
 *
 * ⚠️ Two reasons, and both bite. A retired period is a **422 on `periodId`** at
 * write time (`assertPeriodUsable`), so only the active ones may be offered —
 * and filtering server-side would make "none exists" and "every one is retired"
 * arrive as the identical empty list, which are two different problems with two
 * different next steps, one of which is on this very screen.
 */
type PeriodsState =
  | { kind: 'loading' }
  | { kind: 'ready'; active: TargetPeriodRecord[]; retired: number }
  | { kind: 'error'; message: string };

/**
 * The groups, fetched UNFILTERED for the same reason the periods are: a retired
 * group is a 422 on `subjectGroupId` (`assertSubjectGroupUsable` in
 * `targets.service.ts:597-609`), so only active ones may be offered, and the
 * retired count is what tells "no group exists" apart from "every one is
 * retired".
 *
 * ⚠️ **No allow-list anywhere in here.** Any group may hold a target — owner's
 * ruling — so the picker offers every active one and the two that cannot
 * attribute are explained rather than withheld.
 */
type GroupsState =
  | { kind: 'loading' }
  | { kind: 'ready'; active: UserGroup[]; all: UserGroup[] }
  | { kind: 'error'; message: string };

type AttributionState =
  | { kind: 'loading' }
  | { kind: 'ready'; attribution: TargetAttribution }
  /** The probe failed or was refused — we do not know, and we say nothing. */
  | { kind: 'unknown' };

export function GroupTargetsCard({ ability }: { ability: Ability }) {
  const [state, setState] = useState<TargetsState>({ kind: 'loading' });
  const [nonce, setNonce] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    // Deferred a tick — same reason as every other list on this page.
    const timer = setTimeout(() => {
      void api
        .listTargets(TARGETS_QUERY, signal)
        .then((result) => {
          if (signal.aborted) return;
          setState({ kind: 'ready', targets: result.data, total: result.meta.total });
        })
        .catch((caught: unknown) => {
          if (signal.aborted) return;
          if (caught instanceof ApiError && caught.status === 403) {
            setState({ kind: 'forbidden' });
            return;
          }
          setState({
            kind: 'error',
            message:
              caught instanceof Error ? caught.message : 'The group targets could not be loaded.',
          });
        });
      /*
       * No `.finally` here, unlike the cards that carry a separate `loading`
       * boolean: `loading` is a MEMBER of this state rather than a flag beside
       * it, so every branch above already lands on a terminal value and there is
       * nothing left to clear. A no-op `.finally` would read as a guard that
       * does something.
       */
    }, 0);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  const total = state.kind === 'ready' ? state.total : null;
  const groupsCovered =
    state.kind === 'ready'
      ? new Set(state.targets.map((target) => target.subjectGroup?.id ?? -1)).size
      : 0;

  return (
    <>
      <CatalogMasterCard
        icon={<GroupTargetIcon />}
        title="Group targets"
        blurb="What a user group has committed to deliver in a window."
        loading={state.kind === 'loading'}
        error={state.kind === 'error' ? state.message : null}
        badges={
          state.kind === 'forbidden' ? (
            // Not an error, and not a count of zero: the server declined to say.
            <Badge tone="neutral" variant="outline">
              Not visible to this account
            </Badge>
          ) : (
            <>
              <Badge tone="neutral" variant="outline">
                {pluralise(total ?? 0, 'target')}
              </Badge>
              {groupsCovered > 0 && (
                <Badge tone="neutral" variant="outline">
                  across {pluralise(groupsCovered, 'group')}
                </Badge>
              )}
            </>
          )
        }
        canWrite={canWriteAnyGroupTarget(ability)}
        onOpen={() => setOpen(true)}
      />

      {open && (
        <GroupTargetsListModal
          ability={ability}
          state={state}
          onClose={() => setOpen(false)}
          onReload={reload}
        />
      )}
    </>
  );
}

function GroupTargetsListModal({
  ability,
  state,
  onClose,
  onReload,
}: {
  ability: Ability;
  state: TargetsState;
  onClose: () => void;
  onReload: () => void;
}) {
  const [periods, setPeriods] = useState<PeriodsState>({ kind: 'loading' });
  const [groups, setGroups] = useState<GroupsState>({ kind: 'loading' });
  const [currencies, setCurrencies] = useState<ComboboxOption[] | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Target | null>(null);
  const [deleting, setDeleting] = useState<Target | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /*
   * ✅ THE GRANT, ASKED AS THE GRANT — corrected 2026-09-04; see this file's
   * header for what this replaced and why the role check was wrong in both
   * directions. Three questions, because a set can answer them differently.
   */
  const canCreate = ability.canCreate('targets');
  const canUpdate = ability.canUpdate('targets');
  const canDelete = ability.canDelete('targets');
  const denial = targetsWriteDenial(ability);

  /*
   * 🔴 **WHAT A READER CALLS EACH BAND, FETCHED — ONCE, HERE, AND PASSED DOWN.**
   *
   * ⛔ **Not `BILLING_REGION_LABELS`, which does not exist.** The names are
   * admin-editable columns on `f_billing_region_defaults`; a literal map in
   * `src/` is a second copy of wording the server owns, right on the day it is
   * written and silently wrong after the first rename.
   *
   * ⚠️ **ONE CALL, NOT ONE PER SECTION, and that is the whole reason it is a
   * prop below rather than a hook in `GroupSection`.** That component renders
   * once per group, so a hook inside it would be a request per group on every
   * open, to fetch four names that cannot differ between one call and the next.
   *
   * ⚠️ *SUPERSEDED: "against a 120-per-60s per-IP budget shared across the
   * office."* It is **12,000/60s** (corrected 2026-09-17). Fetching once is
   * still right — a per-group lookup for one unchanging answer is the wrong
   * shape at any ceiling — but the arithmetic that reason cited is off by a
   * factor of a hundred.
   *
   * ⚠️ **A failed read falls back to the raw slug and says nothing**, which is
   * the resolver's own rule: this is a label lookup, and the targets still load,
   * save and measure without it. `united_kingdom` in place of "United Kingdom"
   * is unmistakable and is exactly the string somebody needs in order to go and
   * look.
   */
  const regionLabel = useBillingRegionLabels();

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    void api
      .listTargetPeriods({ perPage: PERIODS_PAGE_SIZE }, signal)
      .then((result) => {
        if (signal.aborted) return;
        setPeriods({
          kind: 'ready',
          active: result.data.filter((period) => period.isActive),
          retired: result.data.filter((period) => !period.isActive).length,
        });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setPeriods({
          kind: 'error',
          message:
            caught instanceof Error ? caught.message : 'The target periods could not be loaded.',
        });
      });

    void api
      /*
       * ⚠️ UNPAGINATED — `{"data": [...]}` with no `meta`, so there is no pager
       * here and no `perPage` to send. `isActive` is opt-in and deliberately
       * omitted; see `GroupsState`.
       */
      .listUserGroups({}, signal)
      .then((result) => {
        if (signal.aborted) return;
        setGroups({
          kind: 'ready',
          active: result.data.filter((group) => group.isActive),
          all: result.data,
        });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setGroups({
          kind: 'error',
          message: caught instanceof Error ? caught.message : 'The user groups could not be loaded.',
        });
      });

    void api
      .listCurrencies({ perPage: CURRENCY_PAGE_SIZE, isActive: true }, signal)
      .then((result) => {
        if (signal.aborted) return;
        setCurrencies(
          result.data.map((currency) => ({
            value: currency.currencyCode,
            label: currency.currencyCode,
            hint: currency.currencyName,
          })),
        );
      })
      .catch(() => {
        // Deliberately silent, exactly as `UserTargetsModal` is: a failed
        // currency read is not an error about targets, and the control degrades
        // to a typed three-letter code rather than disappearing.
        if (!signal.aborted) setCurrencies(null);
      });

    return () => controller.abort();
  }, []);

  const rows = state.kind === 'ready' ? state.targets : NO_TARGETS;

  /*
   * ⚠️ THE WINDOW USED TO ASK ABOUT ATTRIBUTION IS ARBITRARY, AND THAT IS A
   * MEASURED FACT RATHER THAN A CONVENIENCE.
   *
   * `/targets/actuals` requires a window, but `attributionForGroup(id, slug)`
   * consults only the group's slug — nothing in it reads `from`, `to` or the
   * period. So one probe answers "can this group be measured at all" for every
   * window there will ever be, and asking once per group is what keeps this off
   * a request-per-row.
   *
   * ⚠️ *SUPERSEDED: "a request-per-row against a 120/60s bucket."* The limit is
   * **12,000/60s** (corrected 2026-09-17). Asking once per group is still
   * right — the probe's answer does not vary by row, so a per-row lookup is
   * the wrong shape — but the arithmetic that reason cited is off by a factor
   * of a hundred.
   *
   * The FIRST ACTIVE period is used, falling back to any period, because a
   * `periodId` naming no row is a 422 and there is nothing here to catch it
   * with. With no period at all there is no probe — and the modal is already
   * showing the "no target periods" empty state in that case.
   */
  const probePeriodId =
    periods.kind === 'ready' ? (periods.active[0]?.id ?? null) : null;

  const shownGroupIds = useMemo<readonly number[]>(() => {
    const ids = new Set<number>();
    for (const row of rows) if (row.subjectGroup !== null) ids.add(row.subjectGroup.id);
    return [...ids].sort((a, b) => a - b);
  }, [rows]);

  const attribution = useGroupAttribution(shownGroupIds, probePeriodId);

  const sections = useMemo(() => groupSections(rows, groups), [rows, groups]);

  const initialLoad = state.kind === 'loading';
  const noPeriods = periods.kind === 'ready' && periods.active.length === 0;
  const noGroups = groups.kind === 'ready' && groups.active.length === 0;
  /*
   * The form is only offered once there is something to point a target at AND
   * somebody to point it at. Both are separate from "could not be read", which
   * is an unknown rather than an absence.
   */
  const formUsable =
    canCreate &&
    periods.kind === 'ready' &&
    periods.active.length > 0 &&
    groups.kind === 'ready' &&
    groups.active.length > 0;

  async function confirmDelete() {
    if (!deleting || busy) return;
    setBusy(true);
    setDeleteError(null);
    try {
      await api.deleteTarget(deleting.id);
      setDeleting(null);
      onReload();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 404) {
        // Already gone: that is the outcome the reader asked for, not an error.
        setDeleting(null);
        onReload();
      } else if (caught instanceof ApiError) {
        setDeleteError(
          caught.status === 403
            ? `${targetsWriteDenial(ability) ?? 'The server refused that.'} Nothing was deleted.`
            : caught.message,
        );
      } else {
        setDeleteError('Couldn’t reach the server. Nothing was deleted.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title="Group targets"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            {formUsable && (
              <Button
                variant="primary"
                leadingIcon={<PlusIcon />}
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
              >
                New group target
              </Button>
            )}
          </>
        }
      >
        <div className="grid gap-3">
          {/*
            ⚠️ REMOVED 2026-09-05, and the group PICKER's design leant on it.
            The note said: any group can hold a target, only `account_lead` and
            `client_success_manager` can have a figure attributed to one, and a
            target on any other group is real but unmeasurable. The picker still
            offers EVERY active group deliberately, on the reasoning that this
            was "explained on the list" — that explanation is now gone, so the
            only warning arrives after the target exists, as the server's own
            `attribution.reason` on the row (which is kept).
          */}
          {state.kind === 'forbidden' && (
            <Notice tone="warning" title="These targets could not be shown.">
              This account is not permitted to read targets. Ask an administrator for access.
            </Notice>
          )}

          {state.kind === 'error' && (
            <CatalogErrorBlock>Couldn’t load the group targets. {state.message}</CatalogErrorBlock>
          )}

          {periods.kind === 'error' && (
            <CatalogErrorBlock>
              The target periods could not be loaded. {periods.message} Nothing can be added or
              moved until they are, because every target names one.
            </CatalogErrorBlock>
          )}

          {groups.kind === 'error' && (
            <CatalogErrorBlock>
              The user groups could not be loaded. {groups.message} Nothing can be added until they
              are, because every target here names one.
            </CatalogErrorBlock>
          )}

          {denial && <p className="m-0 text-xs text-fg-subtle">{denial} You can read it here.</p>}

          {initialLoad ? (
            <ul className="m-0 grid list-none gap-2 p-0">
              {Array.from({ length: SKELETON_ROWS }, (_, index) => (
                <li key={index} className="grid gap-2 rounded-lg border border-border px-3 py-2.5">
                  <Skeleton className="h-3 w-40" />
                  <Skeleton className="h-2.5 w-56" />
                </li>
              ))}
            </ul>
          ) : (
            <>
              {/*
                ⚠️ THE DAY-ONE STATE, AND IT IS NOT "no targets set".
                `f_target_periods` can be empty, and a target with no window to
                name cannot be created at all. Wording this as an empty target
                list would invite somebody to press a button that cannot succeed.
                Two causes, two sentences — which is the whole reason the periods
                are fetched unfiltered.
              */}
              {noPeriods && (
                <EmptyState
                  icon={<GroupTargetIcon />}
                  title={
                    periods.kind === 'ready' && periods.retired > 0
                      ? 'Every target period has been retired'
                      : 'No target periods exist yet'
                  }
                  description={
                    periods.kind === 'ready' && periods.retired > 0
                      ? `All ${periods.retired} are retired. Restore or add one on the Target periods card, on this screen.`
                      : 'Add one on the Target periods card, on this screen. Every target names a window.'
                  }
                />
              )}

              {!noPeriods && noGroups && (
                <EmptyState
                  icon={<GroupTargetIcon />}
                  title="No user group is available"
                  description="Every group is retired, or none has been created. A retired group cannot be given a target."
                />
              )}

              {!noPeriods && !noGroups && sections.length === 0 && state.kind === 'ready' && (
                <EmptyState
                  icon={<GroupTargetIcon />}
                  title="No group targets set"
                  description={
                    formUsable
                      ? 'Add one to commit a group to a figure for a window.'
                      : `${denial ?? ''} A group target commits a group to a figure for a window.`
                  }
                />
              )}

              {sections.map((section) => (
                <GroupSection
                  key={section.key}
                  section={section}
                  attribution={attribution[section.groupId] ?? { kind: 'loading' }}
                  regionLabel={regionLabel}
                  canUpdate={canUpdate}
                  canDelete={canDelete}
                  busy={busy}
                  onEdit={(target) => {
                    setEditing(target);
                    setFormOpen(true);
                  }}
                  onDelete={(target) => {
                    setDeleteError(null);
                    setDeleting(target);
                  }}
                />
              ))}
            </>
          )}
        </div>
      </Modal>

      {formOpen && groups.kind === 'ready' && periods.kind === 'ready' && (
        <GroupTargetFormModal
          record={editing}
          groups={groups.active}
          allGroups={groups.all}
          periods={periods.active}
          currencies={currencies}
          regionLabel={regionLabel}
          ability={ability}
          onClose={() => setFormOpen(false)}
          onSaved={() => {
            setFormOpen(false);
            onReload();
          }}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => {
          setDeleting(null);
          setDeleteError(null);
        }}
        onConfirm={() => void confirmDelete()}
        title="Remove this group target?"
        confirmLabel="Remove target"
        loading={busy}
        error={deleteError}
        description={
          deleting === null ? null : (
            <>
              {/*
                ⚠️ **THE BANDS ARE NAMED HERE FOR THE SAME REASON THEY ARE ON THE
                ROW, one step more urgently**: several rows can differ in nothing
                else, and this dialog is where somebody destroys one of them.
                Naming the record and the real consequence is what a
                `ConfirmDialog` is for.

                ⛔ **`billingRegions`, NEVER `billingRegion`** — the singular is
                the stored column and is a comma-joined string on a multi-band
                row, so a label lookup on it prints an unrecognised slug in the
                one dialog that must name exactly what is being destroyed.

                ⚠️ **Parenthesised rather than read as "in ⟨band⟩".** Four of the
                five labels are places and the fifth is a sentence — *No billing
                region recorded* — so a preposition in front of it produces
                English that reads as a fault in the app. A parenthetical takes
                all five, and a list of them.
              */}
              Removes the {targetMetricLabel(deleting.metric).toLowerCase()} target of{' '}
              {formatTarget(deleting)} for {deleting.subjectGroup?.name ?? 'this group'}{' '}
              {describeBands(deleting.billingRegions, regionLabel) !== null && (
                <>({describeBands(deleting.billingRegions, regionLabel)}) </>
              )}
              over {describeTargetPeriod(deleting.period)}. Cannot be undone.
            </>
          )
        }
      />
    </>
  );
}

/* -------------------------------------------------------------------------
 * Attribution: one probe per group, cached for the life of the modal.
 * --------------------------------------------------------------------- */

/**
 * Whether each group's figures can be computed at all, asked of the server.
 *
 * ⚠️ **Asked, never derived.** The decision belongs to
 * `RELATIONSHIP_BY_GROUP_SLUG` in the backend's `target-attribution.ts`, and it
 * is decided by which columns the revenue documents carry — a schema fact this
 * app cannot see. A client that matched on the slug would hold a second copy of
 * a server join key in a place nothing keeps in step.
 *
 * A failed or refused probe resolves to `unknown`, and `unknown` renders
 * NOTHING. Saying "this cannot be measured" because a request did not come back
 * would be a claim about the data made from a claim about the network.
 */
function useGroupAttribution(
  groupIds: readonly number[],
  probePeriodId: number | null,
): Record<number, AttributionState> {
  const [states, setStates] = useState<Record<number, AttributionState>>({});
  /*
   * Which ids a request has already been fired for, so a re-render cannot fire a
   * second. A ref rather than state: it must not itself cause a render, and it
   * must be readable synchronously inside the effect that writes it.
   *
   * ⚠️ `groupIds` must be a STABLE identity — it is memoised by the caller — or
   * this effect re-runs on every render. The set is what stops that becoming a
   * request storm, but a re-run would still abort every probe in flight.
   */
  const requested = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (probePeriodId === null) return;
    /*
     * Read into a local, because the cleanup below must act on the SAME set this
     * run added to. A ref read in cleanup is the ref as it stands then, which is
     * the thing that quietly stops being true when a component re-mounts.
     */
    const fired = requested.current;
    const missing = groupIds.filter((id) => !fired.has(id));
    if (missing.length === 0) return;

    const controller = new AbortController();
    const { signal } = controller;
    const settled = new Set<number>();

    for (const id of missing) {
      fired.add(id);
      setStates((previous) => ({ ...previous, [id]: { kind: 'loading' } }));

      void api
        .targetActuals(
          { subjectType: 'user_group', subjectGroupId: id, periodId: probePeriodId },
          signal,
        )
        .then((result) => {
          if (signal.aborted) return;
          settled.add(id);
          setStates((previous) => ({
            ...previous,
            [id]: { kind: 'ready', attribution: result.data.attribution },
          }));
        })
        .catch(() => {
          if (signal.aborted) return;
          settled.add(id);
          setStates((previous) => ({ ...previous, [id]: { kind: 'unknown' } }));
        });
    }

    return () => {
      controller.abort();
      /*
       * An aborted probe never settled, so its id must leave the fired set or it
       * stays `loading` for ever — a skeleton that never resolves, which reads
       * as a hung screen rather than as a cancelled request.
       */
      for (const id of missing) if (!settled.has(id)) fired.delete(id);
    };
  }, [groupIds, probePeriodId]);

  return states;
}

/* -------------------------------------------------------------------------
 * Rendering
 * --------------------------------------------------------------------- */

interface Section {
  key: string;
  /** `-1` when the row's group did not resolve — see `groupSections`. */
  groupId: number;
  group: TargetSubjectGroup | null;
  /** The master row, when the group is still in the master. */
  master: UserGroup | null;
  targets: Target[];
}

/**
 * The rows, grouped by the group they name.
 *
 * Sections come out in the MASTER's own order (`sort_order, name`, as served),
 * with any group the master no longer carries appended — retired rows are in the
 * master, so this only catches a group deleted out from under a target, which
 * the foreign key makes unreachable. It is here because a row must never be
 * dropped for being unexpected: an unresolvable group gets a section that says
 * so rather than vanishing from a list somebody is counting.
 */
function groupSections(rows: readonly Target[], groups: GroupsState): Section[] {
  const byGroup = new Map<number, Target[]>();
  const seen = new Map<number, TargetSubjectGroup | null>();

  for (const row of rows) {
    const id = row.subjectGroup?.id ?? -1;
    const bucket = byGroup.get(id);
    if (bucket === undefined) byGroup.set(id, [row]);
    else bucket.push(row);
    if (!seen.has(id)) seen.set(id, row.subjectGroup);
  }

  const masterOrder = groups.kind === 'ready' ? groups.all : [];
  const sections: Section[] = [];
  const taken = new Set<number>();

  for (const master of masterOrder) {
    const targets = byGroup.get(master.id);
    if (targets === undefined) continue;
    taken.add(master.id);
    sections.push({
      key: `g${master.id}`,
      groupId: master.id,
      group: seen.get(master.id) ?? null,
      master,
      targets,
    });
  }

  for (const [id, targets] of byGroup) {
    if (taken.has(id)) continue;
    sections.push({
      key: `g${id}`,
      groupId: id,
      group: seen.get(id) ?? null,
      master: null,
      targets,
    });
  }

  return sections;
}

function GroupSection({
  section,
  attribution,
  regionLabel,
  canUpdate,
  canDelete,
  busy,
  onEdit,
  onDelete,
}: {
  section: Section;
  attribution: AttributionState;
  /**
   * What a reader calls each band, from the master.
   *
   * ⚠️ **A PROP RATHER THAN `useBillingRegionLabels()` HERE, and that is a
   * request count rather than a preference.** This component renders once per
   * group, so a hook inside it would fire one fetch per section on every open to
   * resolve four names that cannot differ between them. The modal calls it once
   * and hands the resolver down.
   */
  regionLabel: BillingRegionLabeller;
  /**
   * `targets.update` and `targets.delete`, separately — a set can grant one and
   * refuse the other, so a single `canWrite` would be wrong one way or the
   * other for anybody holding such a set.
   */
  canUpdate: boolean;
  canDelete: boolean;
  busy: boolean;
  onEdit: (target: Target) => void;
  onDelete: (target: Target) => void;
}) {
  const name = section.group?.name ?? section.master?.name ?? 'Group not resolved';
  const slug = section.group?.slug ?? section.master?.slug ?? null;
  const retired = section.master !== null && !section.master.isActive;

  return (
    <section className="grid gap-2 rounded-lg border border-border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="m-0 text-sm font-semibold text-fg">{name}</h3>
        {slug !== null && (
          <span className="tabular text-xs text-fg-muted">{slug}</span>
        )}
        {retired && <Badge tone="neutral">Retired</Badge>}
      </div>

      <AttributionNote state={attribution} />

      {/*
        🔴 **A FACT ABOUT THE DATA, AND DELIBERATELY NOT A REFUSAL.** Two band
        sets on one metric and one window may legitimately intersect — they are
        different rows under the unique key and the owner decided they are
        allowed — but nothing resolves two targets, so their actuals count the
        same invoices against BOTH. A reader adding those two figures together is
        the wrong conclusion this prevents, and nothing else on screen can say so:
        each row is internally correct and the overlap only exists BETWEEN them.

        ⚠️ **NOT HELPER TEXT — it is the documented fourth kind**, a fact the
        reader cannot otherwise see. A sweep must not delete it.

        ⛔ **And it must never become a client-side overlap CHECK.** A frontend
        convention presented as a rule is what this repo refuses everywhere, and
        here it would refuse the very rows the multi-band decision exists to hold
        — the same ruling taken on overlapping target periods.
      */}
      {bandsOverlapWithin(section.targets) && (
        <p className="m-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
          Two of these cover some of the same billing bands for one metric and window, so those
          invoices are counted towards both.
        </p>
      )}

      <ul className="m-0 grid list-none gap-2 p-0">
        {section.targets.map((target) => (
          <li
            key={target.id}
            className="grid gap-2 rounded-md border border-border bg-surface-sunken px-3 py-2.5 sm:grid-cols-[1fr_auto] sm:items-center"
          >
            <div className="min-w-0">
              <p className="m-0 text-sm font-medium text-fg">
                {targetMetricLabel(target.metric)} ·{' '}
                <span className="tabular">{formatTarget(target)}</span>
              </p>
              {/*
                🔴 **THE BANDS ARE ON THE ROW BECAUSE TWO ROWS WOULD OTHERWISE BE
                IDENTICAL.** The set is part of the unique key, so one group can
                hold the same metric for the same period over several different
                sets of bands — and without this those rows differ in nothing a
                reader can see. They would read as duplicates, and the obvious
                response to a duplicate is to remove one.

                ⚠️ **A FACT ABOUT THE ROW, not helper text**, which is the
                documented exception: the wrong conclusion it prevents is "one of
                these is a mistake".

                ⛔ **`billingRegions`, NEVER `billingRegion`.** The singular is a
                proxy field — the stored column verbatim, which is a comma-joined
                string on a multi-band row — so `regionLabel(target.billingRegion)`
                would print `europe,united_kingdom` as an unrecognised slug on
                exactly the rows this line exists to tell apart.

                ⚠️ **`null` prints NOTHING rather than a band** — see
                `describeBands`, which owns that rule so the row and the delete
                dialog cannot word one absence two ways.
              */}
              <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-fg-muted">
                <span className="[overflow-wrap:anywhere]">{describeTargetPeriod(target.period)}</span>
                {target.billingRegions !== null && target.billingRegions.length > 0 && (
                  <>
                    <span aria-hidden="true">·</span>
                    <ValuePills
                      items={namePills(target.billingRegions.map((band) => regionLabel(band)))}
                    />
                  </>
                )}
              </div>
            </div>
            {(canUpdate || canDelete) && (
              <div className="flex items-center gap-1.5 sm:justify-end">
                {canUpdate && (
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    aria-label={`Edit the ${targetMetricLabel(target.metric).toLowerCase()} target for ${name}`}
                    title="Edit"
                    disabled={busy}
                    leadingIcon={<PencilIcon />}
                    onClick={() => onEdit(target)}
                  />
                )}
                {canDelete && (
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    aria-label={`Remove the ${targetMetricLabel(target.metric).toLowerCase()} target for ${name}`}
                    title="Remove"
                    disabled={busy}
                    leadingIcon={<TrashIcon />}
                    className="text-fg-subtle hover:text-danger"
                    onClick={() => onDelete(target)}
                  />
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Whether this group's figures can be computed, in the server's own words.
 *
 * ⚠️ **THE THREE STATES MUST LOOK DIFFERENT, and two of them are routinely
 * collapsed into a zero.** "We cannot measure this" is not "they achieved
 * nothing", and neither is "we have not asked yet". There is no bar, no
 * percentage and no dash anywhere in here for exactly that reason — a
 * zero-width meter under an unattributable group reads as total failure against
 * a commitment somebody deliberately made.
 *
 * ⚠️ **`reason` is printed VERBATIM.** It is composed in
 * `unattributableReason()` server-side, and shortening it here would put this
 * app back in the business of knowing which groups attribute.
 */
function AttributionNote({ state }: { state: AttributionState }) {
  if (state.kind === 'loading') return <Skeleton className="h-3 w-56" />;
  /*
   * A probe that failed tells us nothing about the data, so it says nothing. The
   * alternative — "this group cannot be measured" on the strength of a request
   * that did not come back — is a claim about the business made from a claim
   * about the network.
   */
  if (state.kind === 'unknown') return null;

  const { attribution } = state;

  if (attribution.kind === 'unattributable') {
    return (
      <Notice tone="warning">
        <span className="break-words">{attribution.reason}</span>
      </Notice>
    );
  }

  /*
   * The attributable branch said "Measured through the account lead." (or the
   * client success manager) and was removed on 2026-09-05 — it is not an error,
   * a refusal, a ConfirmDialog or an EmptyState. The UNATTRIBUTABLE branch above
   * is kept by name and still prints the server's own `reason`.
   */
  if (attribution.kind === 'group') return null;

  /*
   * `organisation` and `user` cannot come back from a group query, and an
   * unrecognised member means the contract is newer than this build. Both say
   * nothing rather than guessing — the same rule as `xero.ts`'s unrecognised
   * refusal code.
   */
  return null;
}

/* -------------------------------------------------------------------------
 * The form
 * --------------------------------------------------------------------- */

interface FormValues {
  /** The `<select>` value: a stringified group id, or '' for none chosen. */
  subjectGroupId: string;
  metric: TargetMetric;
  /** The `<select>` value: a stringified period id, or '' for none chosen. */
  periodId: string;
  targetAmount: string;
  /** Always held, even for a count — the control is hidden, not the state. */
  baseCurrency: string;
  /**
   * REQUIRED on a team target — at least one band — and `[]` means nobody has
   * ticked anything yet.
   *
   * 🔴 **THREE THINGS SPELL AS "EMPTY-ISH" HERE AND ALL THREE ARE DIFFERENT
   * FACTS**, which is why this is an array of the closed union and not a string:
   *
   * ```
   * null           NOT REGION SCOPED  — a user or organisation target. The wire
   *                                     can hold it; this FORM never produces it
   * []             nobody has ticked yet — a draft state, refused by validate()
   * ['none']       the UNBANDED COMPANIES — a real, deliberate commitment
   * ```
   *
   * ⚠️ **`[]` IS NOT `['none']`, and `[]` IS NOT `null`.** `'none'` is the
   * unbanded band — companies with no country recorded — and is a commitment
   * somebody made; `[]` is the control before it has been touched; `null` is a
   * different subject type entirely. `validate()` refuses `[]`, and
   * `billingRegionsFor` maps it to the `null` the server refuses rather than
   * inventing a band.
   *
   * ⛔ **There is deliberately no default.** Every band would be a commitment
   * the reader never made, and `'none'` would be the worst of them — a target
   * silently attached to the companies nobody has classified.
   */
  billingRegions: TargetBillingRegion[];
}

function blankForm(): FormValues {
  return {
    subjectGroupId: '',
    metric: 'booked',
    periodId: '',
    targetAmount: '',
    baseCurrency: '',
    billingRegions: [],
  };
}

function recordForm(target: Target): FormValues {
  return {
    subjectGroupId: target.subjectGroup === null ? '' : String(target.subjectGroup.id),
    metric: target.metric,
    periodId: String(target.period.id),
    targetAmount: target.targetAmount,
    baseCurrency: target.baseCurrency ?? '',
    /*
     * ⛔ **`billingRegions`, NEVER `billingRegion`** — the singular is a proxy
     * field and on a multi-band row it is a comma-joined string, which as a
     * picker value would be an option that matches nothing and would be dropped
     * by the next save. The write is a whole-set REPLACE, so a band the control
     * cannot show is a band the save removes.
     *
     * ⚠️ `null` maps to `[]`, which reads as "nothing ticked" and is REFUSED on
     * the next save. That is the honest rendering of a stored row predating the
     * column: it names no bands, and coalescing it to `['none']` would silently
     * attach somebody's existing commitment to the unbanded companies.
     *
     * ⚠️ **COPIED, not aliased.** The array is mutated by nothing here, but the
     * baseline is a second `recordForm(record)` call and both must be
     * independent of the row so an edit cannot rewrite what it is diffed
     * against.
     */
    billingRegions: target.billingRegions === null ? [] : [...target.billingRegions],
  };
}

/**
 * The SINGULAR key, which the server may still name in a refusal this form has a
 * control for.
 *
 * ⚠️ **The stored column is `billing_region` and the post-merge pair refusal is
 * worded against it**, so a 422 can arrive naming `billingRegion` while the only
 * control on screen is the plural one. Left unroutable it would reach the banner
 * and highlight nothing; `handleError` folds it onto `billingRegions` instead.
 */
const LEGACY_REGION_KEY = 'billingRegion';

/**
 * The write keys a 422 may be MARKED against.
 *
 * Derived from the form's own value object rather than written out, so it cannot
 * drift from the controls. `subjectType` and `subjectId` are real write keys and
 * are deliberately ABSENT: no control here shows either, so a refusal naming one
 * must reach the banner — routed to a field it would vanish from the banner,
 * inflate the "one field needs attention" count and highlight nothing.
 *
 * ⚠️ **{@link LEGACY_REGION_KEY} is the ONE key added by hand**, and it is added
 * precisely because the form does NOT render a control called `billingRegion`
 * while the server may still name one. Everything else stays derived.
 */
const KNOWN_WRITE_KEYS: readonly string[] = [...Object.keys(blankForm()), LEGACY_REGION_KEY];

function GroupTargetFormModal({
  record,
  groups,
  allGroups,
  periods,
  currencies,
  regionLabel,
  ability,
  onClose,
  onSaved,
}: {
  record: Target | null;
  /** Active groups only — a retired one is a 422 on `subjectGroupId`. */
  groups: UserGroup[];
  /** Every group, so a stored retired one can be labelled rather than dropped. */
  allGroups: UserGroup[];
  /** Active periods only — a retired one is a 422 on `periodId`. */
  periods: TargetPeriodRecord[];
  currencies: ComboboxOption[] | null;
  /**
   * What a reader calls each band, from the master — see the modal that owns the
   * fetch. Passed in so the list and the form cannot name one band two ways.
   */
  regionLabel: BillingRegionLabeller;
  ability: Ability;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = record !== null;
  const [values, setValues] = useState<FormValues>(() =>
    record === null ? blankForm() : recordForm(record),
  );
  const [baseline] = useState<FormValues | null>(() =>
    record === null ? null : recordForm(record),
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const moneyMetric = metricIsMoney(values.metric);

  /*
   * A group the row points at but the picker does not offer — a retired one, or
   * one the master no longer carries. Injected for the same reason
   * `withStoredOption()` exists: without it an existing target's group control
   * reads as "none chosen", and the next save would move the target to whatever
   * the reader happened to pick instead.
   *
   * ⚠️ It is offered as SELECTED and labelled, not offered as a choice: the
   * server refuses a retired group on write, so saving an unchanged row that
   * names one is a 422 either way — and the label is what makes that refusal
   * make sense when it arrives.
   */
  const storedGroupId = record?.subjectGroup?.id ?? null;
  const missingGroup =
    storedGroupId !== null && !groups.some((group) => group.id === storedGroupId)
      ? (allGroups.find((group) => group.id === storedGroupId) ?? record?.subjectGroup ?? null)
      : null;

  const missingPeriod =
    record !== null && !periods.some((period) => period.id === record.period.id)
      ? record.period
      : null;

  const currencyOptions = useMemo<ComboboxOption[]>(() => {
    if (currencies === null) return [];
    const stored = values.baseCurrency.trim().toUpperCase();
    if (stored === '' || currencies.some((option) => option.value === stored)) return currencies;
    return [
      { value: stored, label: stored, hint: 'Not in the currency list — kept as it is' },
      ...currencies,
    ];
  }, [currencies, values.baseCurrency]);

  /**
   * The five bands, with the unbanded one LAST and any held slug this bundle
   * does not know appended rather than dropped.
   *
   * ⚠️ **THE HELD-VALUE INJECTION IS LOAD-BEARING, not defensive tidiness.** The
   * write is a whole-set REPLACE, so a band the picker cannot show is a band the
   * next save REMOVES — silently, with a `200`. `TargetBillingRegion` is a closed
   * union, so a sixth band the server ships before this bundle does is a state
   * the types say cannot happen and the wire can produce anyway.
   *
   * ⚠️ **`'none'` SORTS LAST by an explicit test against
   * {@link BILLING_REGION_NONE}**, not by its position in a list somebody may
   * reorder. It is the catch-all, and it reads as one only at the foot.
   *
   * ⛔ **NO LABEL MAP ANYWHERE.** The four names are admin-editable columns on
   * `f_billing_region_defaults`, resolved through `regionLabel`; a literal
   * `{united_kingdom: 'United Kingdom', …}` here would be a second copy of
   * wording the server owns — right on the day and silently wrong after the
   * first rename.
   */
  const regionOptions = useMemo<MultiComboboxOption[]>(() => {
    const ordered: TargetBillingRegion[] = [
      ...TARGET_BILLING_REGIONS.filter((band) => band !== BILLING_REGION_NONE),
      ...TARGET_BILLING_REGIONS.filter((band) => band === BILLING_REGION_NONE),
    ];
    for (const held of values.billingRegions) {
      if (!ordered.includes(held)) ordered.push(held);
    }
    return ordered.map((band) =>
      band === BILLING_REGION_NONE
        ? { value: band, label: regionLabel(band), hint: UNBANDED_BAND_HINT }
        : { value: band, label: regionLabel(band) },
    );
  }, [values.billingRegions, regionLabel]);

  function setField<K extends keyof FormValues>(key: K, value: FormValues[K]) {
    setValues((previous) => ({ ...previous, [key]: value }));
    setFieldErrors((previous) => {
      if (!(key in previous)) return previous;
      const next = { ...previous };
      delete next[key as string];
      return next;
    });
  }

  const patch = baseline === null ? null : buildPatch(values, baseline);
  // Derived from the payload itself, so "Save is enabled" and "there is
  // something to send" cannot disagree — an empty PATCH is a guaranteed 422.
  const dirty = patch === null || Object.keys(patch).length > 0;

  function validate(): Record<string, string> {
    const found: Record<string, string> = {};

    if (values.subjectGroupId.trim() === '') {
      found.subjectGroupId = 'Choose the group this target belongs to.';
    }
    if (values.periodId.trim() === '') found.periodId = 'Choose the period this target covers.';
    /*
     * ⚠️ Refused for EVERY metric, including the count. The bands say which
     * companies a commitment is about; whether the figure is money or a number
     * of deals has nothing to do with it, so this is not paired with
     * `moneyMetric` the way the currency below is.
     *
     * ⛔ **CLEARING EVERY BAND IS A REFUSAL, NOT "not scoped".** A team target
     * with no band is a 422 server-side — checked post-merge beside
     * `subjectType` — because there IS no overall team target. Anticipating it
     * here means the reader meets a sentence naming the control rather than a
     * round trip; the server's own sentence still renders if one arrives anyway,
     * because a stale bundle and a changed rule are both reachable.
     */
    if (values.billingRegions.length === 0) {
      found.billingRegions = 'Choose at least one billing region this target covers.';
    }

    const amount = values.targetAmount.trim();
    if (amount === '') {
      found.targetAmount = moneyMetric ? 'Enter the target amount.' : 'Enter the number of deals.';
    } else if (!isTargetAmount(amount)) {
      found.targetAmount = 'Enter a positive figure with at most two decimal places.';
    }

    if (moneyMetric) {
      const code = values.baseCurrency.trim().toUpperCase();
      if (code === '') found.baseCurrency = 'Choose the currency this target is set in.';
      else if (!CURRENCY_PATTERN.test(code)) {
        found.baseCurrency = 'A currency is a three-letter code, such as GBP.';
      }
    }

    return found;
  }

  function handleError(caught: unknown) {
    if (!(caught instanceof ApiError)) {
      setFormError('Couldn’t reach the server. Nothing was saved.');
      return;
    }

    if (caught.status === 403) {
      setFieldErrors({});
      /*
       * ⚠️ The trailing clause used to read "Setting a target is restricted to
       * administrators", which is FALSE: `/targets` has no `requireRole` and
       * the write is decided by the permission set. Saying so sent an
       * administrator whose grant had been withdrawn to ask for a tier they
       * already hold.
       */
      setFormError(
        `${targetsWriteDenial(ability) ?? 'The server refused that.'} Nothing was saved.`,
      );
      return;
    }

    if (caught.status === 404) {
      setFieldErrors({});
      setFormError(
        isEdit
          ? 'This target no longer exists — somebody may have removed it. Close this and reload the list.'
          : 'The server could not find the period or the group this target is for. Close this and reload the list.',
      );
      return;
    }

    const { fields, unroutable } = caught.routeFieldErrors(KNOWN_WRITE_KEYS);
    /*
     * ⚠️ **THE SERVER MAY NAME EITHER KEY, AND BOTH BELONG ON THE ONE CONTROL
     * THAT EXISTS.** `billingRegions` is what this form sends; `billingRegion` is
     * the stored column, and the post-merge pair refusal — a team target with no
     * band, or a subject-type change that leaves one stranded — is worded against
     * it. Left as its own entry it would mark a control that does not exist, so
     * it is FOLDED onto the plural.
     *
     * ⚠️ **The plural wins a collision**, because it names the control the reader
     * is looking at. Both arriving at once is not a state the server produces
     * today; deciding it here rather than letting object order decide is what
     * stops that becoming a silent coin-toss if it ever does.
     *
     * ⚠️ **The pair is judged POST-MERGE, so a refusal can name a field the
     * reader did not touch** — which is exactly why it is routed to a field
     * rather than dropped into the banner where nothing is highlighted.
     */
    const legacyRegionMessage = fields[LEGACY_REGION_KEY];
    if (legacyRegionMessage !== undefined) {
      delete fields[LEGACY_REGION_KEY];
      fields.billingRegions = fields.billingRegions ?? legacyRegionMessage;
    }
    setFieldErrors(fields);

    const lines: string[] = [];
    if (caught.status === 409) {
      /*
       * ⚠️ THE KEY INCLUDES THE BAND SET AND THE CURRENCY, so a different set of
       * bands — or a second currency — for the same group, metric and period is
       * ORDINARY and must not be reported as a clash. Migration 091 widened
       * `(subject, metric, period)` to carry `base_currency`; the region widened
       * it again, on the owner's decision that a team commits per region rather
       * than overall. `findTargetsFor` matches on BOTH generated folds —
       * `subject_key` and `subject_group_key` — so two different groups do not
       * collide either.
       *
       * ⚠️ **THIS SENTENCE SAID "A different currency is fine" AND THAT BECAME
       * HALF THE TRUTH.** A different set of BANDS is fine too, and the old
       * wording would have talked somebody out of the very thing the bands exist
       * for — a second target on the same metric and period over other bands.
       *
       * 🔴 **AND SINCE 2026-09-15 "a different set" IS THE HONEST WORDING, not "a
       * different band".** Band sets may OVERLAP and still be different rows —
       * `['uk','europe']` beside `['europe']` — so a sentence promising that a
       * different BAND is fine would be wrong about exactly the case the
       * multi-band decision introduced, and wrong in the discouraging direction.
       *
       * ⚠️ **Neither the bands nor the period is NAMED, deliberately**, exactly
       * as the group and the period are not: all of them are on screen in the
       * controls the reader has just set, and the actionable half is which row to
       * open and what may legitimately differ.
       *
       * `TARGET_ALREADY_EXISTS` is matched rather than the message, because the
       * server's own sentence predates both widenings and still names only the
       * subject, the metric and the period. Anything else falls through to it.
       */
      const code = values.baseCurrency.trim().toUpperCase();
      lines.push(
        caught.code !== TARGET_ALREADY_EXISTS
          ? 'A target already holds that place — check the list above.'
          : moneyMetric && code !== ''
            ? `${targetMetricLabel(values.metric)} is already targeted for that group, period and set of billing regions in ${code} — open that one and edit it. A different set of regions, or a different currency, is fine.`
            : `${targetMetricLabel(values.metric)} is already targeted for that group, period and set of billing regions — open that one and edit it. A different set of regions is fine.`,
      );
    }
    for (const message of unroutable) if (!lines.includes(message)) lines.push(message);
    /*
     * ⚠️ Not `formMessage ?? message`. Both halves can be true at once — the
     * generic "Validation failed" and the specific detail — and a coalesce
     * between two messages drops one of them silently.
     */
    if (lines.length === 0 && Object.keys(fields).length === 0) lines.push(caught.message);

    setFormError(lines.length > 0 ? lines.join(' ') : null);
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // React propagates by the React tree, so a nested mount would otherwise
    // submit whatever rendered this.
    event.stopPropagation();
    if (saving || !dirty) return;

    const clientErrors = validate();
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      setFormError(null);
      return;
    }

    /*
     * ⚠️ **A GUARD, NOT A CAST, AND IT IS UNREACHABLE.** `validate()` has just
     * refused an empty set, so this cannot fire — writing it out rather than
     * letting an empty array fall through is what keeps the required-ness
     * visible: `billingRegionsFor` would answer `null` for `[]`, which is a real
     * value on the wire meaning *not region scoped*, and a team target carrying
     * it is a 422 naming a field the reader believes they filled in.
     *
     * ⚠️ **It is `.length === 0` rather than a falsiness test**, because an empty
     * array is TRUTHY and the mistake would be silent in the direction that
     * sends the request.
     */
    const bands = values.billingRegions;
    if (bands.length === 0) return;

    setSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      if (record !== null && patch !== null) {
        // Belt and braces: Save is disabled in this state, and an empty PATCH is
        // a guaranteed 422 that must never leave the client.
        if (Object.keys(patch).length === 0) return;
        await api.updateTarget(record.id, patch);
      } else {
        const body: TargetCreateInput = {
          subjectType: 'user_group',
          /*
           * ⚠️ BOTH IDS ARE SENT, and the null is the load-bearing half.
           * `assertSubjectPairing` reads the POST-MERGE state and refuses a
           * `user_group` target that also names a person, so `subjectId: null`
           * is the statement "this belongs to nobody in particular" rather than
           * a key left out.
           */
          subjectId: null,
          subjectGroupId: Number(values.subjectGroupId),
          metric: values.metric,
          periodId: Number(values.periodId),
          /*
           * ⚠️ **THE PLURAL KEY, AND ONLY THE PLURAL KEY.** The server still
           * accepts the singular `billingRegion`, but **sending BOTH in one body
           * is a 422 naming both** — so this sends the array and never both, and
           * `billingRegion` appears nowhere in this payload.
           *
           * ⚠️ **THROUGH `billingRegionsFor`, which defers to the one place the
           * pair rule is decided** — `subjectTypeCarriesBillingRegion`, exactly
           * as `baseCurrencyFor` is the one place for `{metric, baseCurrency}`
           * one line below. `subjectType` is written in this same body, and a
           * caller must never write one without the other: the server validates
           * the POST-MERGE state and refuses the contradiction rather than
           * auto-clearing.
           *
           * It is an identity on this path — a `user_group` target keeps the
           * bands it was given — and that is the point: the form does not get to
           * hold its own opinion about which subjects carry a band.
           *
           * ⚠️ There is no state in which a TEAM target legitimately carries no
           * band: the unbanded companies are `'none'`, a real member. The `null`
           * this can return belongs to the other two subject types, and to an
           * empty set the guard above has already refused.
           */
          billingRegions: billingRegionsFor('user_group', bands),
          // Sent as typed, never re-formatted: rewriting "250000" to
          // "250000.00" would save something other than what was entered.
          targetAmount: values.targetAmount.trim(),
          baseCurrency: baseCurrencyFor(values.metric, values.baseCurrency),
        };
        await api.createTarget(body);
      }
      onSaved();
    } catch (caught) {
      handleError(caught);
    } finally {
      setSaving(false);
    }
  }

  const formId = `group-target-form-${record?.id ?? 'new'}`;

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? 'Edit this group target' : 'New group target'}
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={saving}
            disabled={!dirty}
          >
            {isEdit ? 'Save changes' : 'Add target'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={(event) => void handleSubmit(event)} noValidate className="grid gap-4">
        <FormErrorSummary formError={formError} fieldErrorCount={Object.keys(fieldErrors).length} />

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Group" required error={fieldErrors.subjectGroupId}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={values.subjectGroupId}
                invalid={Boolean(fieldErrors.subjectGroupId)}
                onChange={(event) => setField('subjectGroupId', event.target.value)}
              >
                <option value="">Select a group…</option>
                {missingGroup && (
                  <option value={String(missingGroup.id)}>
                    {missingGroup.name} — no longer offered
                  </option>
                )}
                {/*
                  ⚠️ EVERY ACTIVE GROUP, WITH NO ALLOW-LIST. Any group may hold a
                  target — owner's ruling — and the two that cannot have a figure
                  attributed to them are explained on the list rather than
                  withheld here. Filtering them out would make a legitimate
                  commitment unrecordable and say nothing about why.
                */}
                {groups.map((group) => (
                  <option key={group.id} value={String(group.id)}>
                    {group.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Metric" required error={fieldErrors.metric}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={values.metric}
                invalid={Boolean(fieldErrors.metric)}
                onChange={(event) => setField('metric', event.target.value as TargetMetric)}
              >
                {TARGET_METRICS.map((metric) => (
                  <option key={metric} value={metric}>
                    {targetMetricLabel(metric)}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Period" required error={fieldErrors.periodId}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={values.periodId}
                invalid={Boolean(fieldErrors.periodId)}
                onChange={(event) => setField('periodId', event.target.value)}
              >
                <option value="">Select a period…</option>
                {missingPeriod && (
                  <option value={String(missingPeriod.id)}>
                    {describeTargetPeriod(missingPeriod)} — no longer listed
                  </option>
                )}
                {periods.map((period) => (
                  <option key={period.id} value={String(period.id)}>
                    {describeTargetPeriod(period)}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field
            label={moneyMetric ? 'Target amount' : 'Target number of deals'}
            required
            error={fieldErrors.targetAmount}
          >
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={values.targetAmount}
                inputMode="decimal"
                invalid={Boolean(fieldErrors.targetAmount)}
                onChange={(event) => setField('targetAmount', event.target.value)}
              />
            )}
          </Field>

          {/*
            🔴 **EVERY TEAM TARGET NAMES AT LEAST ONE BAND — owner's decision, and
            SEVERAL since 2026-09-15.** It sits beside Currency because the two
            are the pair that say what a figure is ABOUT: which companies, in what
            money.

            ⚠️ **ALWAYS RENDERED, unlike Currency.** The bands are required for
            the count metric as much as for the money ones — a number of deals
            belongs to a band exactly as an amount does — so this control must
            never be paired with `moneyMetric`.

            🔴 **A `MultiCombobox`, because "UK + EU" is TWO BANDS.** *SUPERSEDED:
            a single-select `Combobox`.* Until that day a target carried exactly
            one band, which is why a regional target covering both could not be
            stored at all.

            ⛔ **AN EMPTY SELECTION IS NOT "every region" AND NOT "no region".**
            There is no all-regions target and no unscoped team target; `'none'`
            is already a real row meaning the unbanded companies. Empty is
            *nothing ticked yet*, `validate()` refuses it, and the placeholder is
            what says so — which is why there is no all-regions row to tell apart
            from `'none'` with nothing on screen to help.

            ⚠️ **`selectAll` is offered** — the owner's 2026-09-13 instruction that
            every multiselect EDIT field carries it. It is safe here because the
            list is complete: five bands, never truncated, so no `footnote` is
            passed and the primitive's completeness interlock has nothing to fire
            on. A target over all five is a real commitment, not a claim about a
            list that was cut short.

            ⚠️ **Options come from `regionOptions`** — `TARGET_BILLING_REGIONS`
            (the four company bands plus `'none'`, composed at
            `@/lib/billing-region` rather than written out here), the unbanded one
            last, and any held slug this bundle does not know appended rather than
            dropped. See that memo: the write is a whole-set replace, so a band
            the picker cannot show is a band the save removes.

            ⚠️ **THE LABELS ARE FETCHED, NOT MAPPED.** `regionLabel` is the
            resolver the modal above fetched once; there is no
            `BILLING_REGION_LABELS` and there must not be, because the four names
            are admin-editable columns the server owns.
          */}
          <Field label="Billing regions" required error={fieldErrors.billingRegions}>
            {({ id, 'aria-describedby': describedBy }) => (
              <MultiCombobox
                id={id}
                aria-describedby={describedBy}
                values={values.billingRegions}
                options={regionOptions}
                placeholder="Select one or more billing regions…"
                searchPlaceholder="Search regions…"
                disabled={saving}
                invalid={Boolean(fieldErrors.billingRegions)}
                selectAll
                /*
                 * ⚠️ **The cast is the ONE place strings become members, and it
                 * is safe by construction**: `MultiCombobox` only ever hands back
                 * values it was given, and every value here comes from
                 * `regionOptions` — the closed vocabulary plus whatever this
                 * form was already holding.
                 */
                onChange={(next) => setField('billingRegions', next as TargetBillingRegion[])}
              />
            )}
          </Field>

          {/*
            ⚠️ HIDDEN, not disabled, for a count — and the state behind it is
            left alone. `baseCurrencyFor` sends null for a count whatever the box
            holds, so hiding cannot leak a currency onto a `won_deals` row, and
            switching back to a money metric restores what was chosen.
          */}
          {moneyMetric ? (
            <Field label="Currency" required error={fieldErrors.baseCurrency}>
              {({ id, 'aria-describedby': describedBy }) =>
                currencies === null ? (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    value={values.baseCurrency}
                    maxLength={3}
                    autoCapitalize="characters"
                    invalid={Boolean(fieldErrors.baseCurrency)}
                    onChange={(event) => setField('baseCurrency', event.target.value.toUpperCase())}
                  />
                ) : (
                  <Combobox
                    id={id}
                    aria-describedby={describedBy}
                    value={values.baseCurrency}
                    options={currencyOptions}
                    placeholder="Select a currency…"
                    searchPlaceholder="Search a code or name…"
                    invalid={Boolean(fieldErrors.baseCurrency)}
                    onChange={(value) => setField('baseCurrency', value)}
                  />
                )
              }
            </Field>
          ) : null}
        </div>

        {isEdit && !dirty && <p className="m-0 text-xs text-fg-muted">No changes yet.</p>}
      </form>
    </Modal>
  );
}

/**
 * The PATCH body: the diff, plus the one pair that cannot be diffed key by key.
 *
 * ⚠️ **`metric` drags `baseCurrency` with it, always.** The server checks the
 * POST-MERGE state, so `{metric: 'won_deals'}` alone is refused for holding a
 * currency the caller never sent — and `{metric: 'booked'}` alone is refused for
 * holding none. Emitting the pair structurally is what makes both directions
 * work; a per-key loop produces a 422 naming a field the user did not touch.
 *
 * ⚠️ **`subjectType` is never sent, and neither `subjectGroupId` nor the bands
 * therefore need a partner here.** This form only ever edits a target that is
 * already a `user_group` one, so both subject pairings are satisfied by the
 * stored row: the group and the bands merely move. Moving a target to a PERSON
 * would need `{subjectType, subjectId, subjectGroupId: null, billingRegions:
 * null}` in one body — two more pairs with the same post-merge rule — and this
 * form deliberately cannot express it, because a group's commitment becoming one
 * person's is a different decision from correcting which group it names.
 */
function buildPatch(next: FormValues, previous: FormValues): TargetUpdateInput {
  const body: TargetUpdateInput = {};

  if (next.metric !== previous.metric) {
    body.metric = next.metric;
    body.baseCurrency = baseCurrencyFor(next.metric, next.baseCurrency);
  } else {
    const after = baseCurrencyFor(next.metric, next.baseCurrency);
    const before = baseCurrencyFor(previous.metric, previous.baseCurrency);
    if (after !== before) body.baseCurrency = after;
  }

  if (next.subjectGroupId !== previous.subjectGroupId && next.subjectGroupId.trim() !== '') {
    body.subjectGroupId = Number(next.subjectGroupId);
  }

  if (next.periodId !== previous.periodId && next.periodId.trim() !== '') {
    body.periodId = Number(next.periodId);
  }

  /*
   * ⛔ **COMPARED BY CONTENT, ORDER-INDEPENDENTLY — see `sameBandSet`.** The
   * server serves the set SORTED and a picker holds it in TICK order, so `!==`
   * on the arrays, or a join-and-compare, would report every loaded target as
   * edited: Save enabled over a form nobody changed, and a band set re-asserted
   * on every unrelated edit.
   *
   * ⚠️ **AN EMPTY SET IS NEVER SENT, and that is the same guard the two ids above
   * carry rather than a new rule.** A team target has no "no bands" state —
   * `'none'` is the unbanded band and is a real member — so an empty value here
   * can only ever be a stored row predating the column, and emitting `null`
   * would be the post-merge refusal rather than a save. `validate()` refuses it
   * before a save can reach this.
   *
   * ⛔ **THE PLURAL KEY, NEVER BOTH.** The server still accepts the singular
   * `billingRegion`, and a body carrying both is a 422 naming both.
   *
   * ⚠️ **NOT routed through `billingRegionsFor`, unlike the CREATE body, and the
   * asymmetry is the pair rule rather than an oversight.** That helper exists
   * because `subjectType` and the bands are checked post-merge and must travel
   * together; this PATCH never sends `subjectType` — the form cannot move a
   * target to a person — so the stored row satisfies the pairing and the bands
   * merely move. Passing it through would be an identity wearing a rule's
   * clothes.
   *
   * ⚠️ **COPIED into the body** so the payload cannot alias the draft a later
   * keystroke mutates.
   */
  if (
    !sameBandSet(next.billingRegions, previous.billingRegions) &&
    next.billingRegions.length > 0
  ) {
    body.billingRegions = [...next.billingRegions];
  }

  const amount = next.targetAmount.trim();
  if (amount !== previous.targetAmount.trim()) body.targetAmount = amount;

  return body;
}
